import { RemoteControl } from "@limrun/ui";
import { instanceToken } from "@limrun/api/instance-token";
import { ChevronDown, Loader2, Smartphone } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { AnalyticsEvents, type StreamPlatform, captureAnalyticsEvent } from "@/analytics";
import { transformUrl, useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization.tsx";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { DeviceIllustration } from "./DeviceIllustration";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toastInstanceCreationError } from "@/components/instance-creation-error";
import { IOS_MODEL_LABEL } from "@/lib/ios-model";

export type OnboardingInstance = {
  id: string;
  platform: StreamPlatform;
  streamUrl?: string;
  token: string;
};

export function OnboardingShell({
  centered,
  children,
}: {
  centered: boolean;
  children?: ReactNode;
}) {
  return (
    <AppShell title="Get started" centered={centered} viewport={!centered}>
      {children}
    </AppShell>
  );
}

export function useOnboardingInstance() {
  const auth = useAuth();
  const [organization] = useOrganization();

  const [isCreating, setIsCreating] = useState(false);
  const [platform, setPlatform] = useState<StreamPlatform>("ios");
  const [instance, setInstance] = useState<OnboardingInstance>();

  const createInstance = async (selectedPlatform: StreamPlatform) => {
    if (isCreating || instance) return;
    captureAnalyticsEvent(AnalyticsEvents.INSTANCE_CREATE_REQUESTED, {
      platform: selectedPlatform,
      device_class: "phone",
      has_initial_assets: false,
      source: "console",
    });
    setPlatform(selectedPlatform);
    setIsCreating(true);
    try {
      if (selectedPlatform === "ios") {
        const result = await auth.regionClient.iosInstances.create({
          wait: true,
          metadata: {
            displayName: "onboarding-simulator",
            labels: { [IOS_MODEL_LABEL]: "iphone" },
          },
          spec: {
            model: "iphone",
          },
        });
        setInstance({
          id: result.metadata.id,
          platform: selectedPlatform,
          streamUrl: result.status.endpointWebSocketUrl,
          token: instanceToken(result),
        });
      } else {
        const result = await auth.regionClient.androidInstances.create({
          wait: true,
          metadata: { displayName: "onboarding-emulator" },
          spec: {
            clues: [{ kind: "OSVersion", osVersion: "15" }],
          },
        });
        setInstance({
          id: result.metadata.id,
          platform: selectedPlatform,
          streamUrl: result.status.endpointWebSocketUrl,
          token: instanceToken(result),
        });
      }
    } catch (error: unknown) {
      toastInstanceCreationError(error);
      setIsCreating(false);
      return;
    }
    setIsCreating(false);
  };

  return {
    ready: organization !== "loading",
    isCreating,
    platform,
    instance,
    createInstance,
  };
}

export function CreateDeviceButton({
  disabled,
  onCreate,
}: {
  disabled: boolean;
  onCreate: (platform: StreamPlatform) => void;
}) {
  return (
    <div className="inline-flex">
      <Button
        size="default"
        className="rounded-r-none px-3"
        disabled={disabled}
        onClick={() => onCreate("ios")}
      >
        <Smartphone className="h-4 w-4" />
        Create iOS Simulator
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="default"
            className="rounded-l-none border-l border-primary-foreground/20 px-2"
            disabled={disabled}
            aria-label="Choose another platform"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onCreate("android")}>
            Create Android Emulator
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function DeviceStreamPane({
  platform,
  instance,
}: {
  platform: StreamPlatform;
  instance: OnboardingInstance | undefined;
}) {
  const streamContainerRef = useRef<HTMLDivElement>(null);
  const connectedRef = useRef(false);

  // Unique per-tab: the simulator tears down existing peer connections when a
  // new offer reuses an id, so a random suffix keeps multi-tab views alive.
  const streamSessionId = useMemo(
    () => `${instance?.id ?? "onboarding"}-${crypto.randomUUID().slice(0, 8)}`,
    [instance?.id],
  );

  useEffect(() => {
    const container = streamContainerRef.current;
    if (!container || !instance?.streamUrl) return;

    const captureConnectedStream = (event: Event) => {
      if (!(event.target instanceof HTMLVideoElement)) return;
      if (connectedRef.current) return;
      connectedRef.current = true;
      captureAnalyticsEvent(AnalyticsEvents.STREAM_CONNECTED, {
        platform: instance.platform,
        source: "console",
      });
    };
    // Media `playing` does not bubble; a capture listener on the container
    // observes the video rendered inside RemoteControl.
    container.addEventListener("playing", captureConnectedStream, true);
    return () => {
      container.removeEventListener("playing", captureConnectedStream, true);
    };
  }, [instance?.streamUrl, instance?.platform]);

  return (
    <div className="relative flex h-[75svh] min-h-96 min-w-0 items-center justify-center overflow-hidden rounded-lg lg:h-[min(80svh,44rem)] lg:min-h-0 lg:self-center">
      {instance?.streamUrl ? (
        <div ref={streamContainerRef} className="contents">
          <RemoteControl
            url={transformUrl(instance.streamUrl)!}
            token={instance.token}
            showFrame
            autoReconnect
            sessionId={streamSessionId}
          />
        </div>
      ) : (
        <div className="text-center">
          <DeviceIllustration className="mx-auto w-80 max-w-full" />
          <Loader2 aria-hidden className="mx-auto mb-3 mt-3 size-4 animate-spin text-brand" />
          <p role="status" className="text-sm text-muted-foreground">
            {platform === "ios"
              ? "Preparing your iOS simulator..."
              : "Preparing your Android emulator..."}
          </p>
        </div>
      )}
    </div>
  );
}
